/**
 * Генерация slug из заголовка сущности (ТЗ SEO-8 §6.2).
 *
 * Slug НЕ хранится: он вычисляется в памяти при построении URL и не является
 * ключом — ключ едет последним сегментом пути. Поэтому от функции требуется не
 * уникальность, а **стабильность**: один заголовок всегда даёт один slug.
 *
 * Лежит в `compob2b-libs`, потому что форму адреса обязаны знать ОБА приложения:
 * витрина строит по ней ссылки, админка показывает будущий адрес подборки в форме.
 * Пока функция жила у витрины, админка завела свою копию — и та разошлась (`й: 'y'`
 * против `й: 'i'`, жёсткий рез вместо границы слова), то есть поле «Адрес страницы»
 * показывало адрес, которого на сайте нет.
 *
 * Импортировать её нужно **конкретным файлом** (`.../compob2b-libs/src/lib/seo/slugify`),
 * а НЕ через алиас `compob2b-libs`: алиас ведёт на barrel `public-api.ts`, и импорт
 * оттуда притащил бы Angular-граф в серверные модули и в node-env спеки (ловушка
 * описана в CLAUDE.md → Testing).
 */

/**
 * Кириллица → латиница. Таблица явная, а не через `String.normalize`:
 * NFD-разложение не трогает кириллицу, а `ж`/`ч`/`ш`/`щ`/`ю`/`я` требуют
 * многобуквенных замен, которых у побуквенного маппинга не бывает.
 *
 * `ъ` и `ь` отображаются в пустую строку осознанно: любой другой вариант
 * («podyezd», «myshj») либо ломает читаемость, либо порождает дефис внутри
 * слова.
 */
const CYRILLIC: Readonly<Record<string, string>> = {
  а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'e', ж: 'zh', з: 'z',
  и: 'i', й: 'i', к: 'k', л: 'l', м: 'm', н: 'n', о: 'o', п: 'p', р: 'r',
  с: 's', т: 't', у: 'u', ф: 'f', х: 'h', ц: 'c', ч: 'ch', ш: 'sh', щ: 'sch',
  ъ: '', ы: 'y', ь: '', э: 'e', ю: 'yu', я: 'ya',
};

/**
 * Потолок длины slug. 80 подтверждён в ТЗ §11.5: при коротких префиксах
 * (`/news/`, `/product/`) и ключе в хвосте весь путь укладывается в ~100
 * символов.
 */
const DEFAULT_MAX_LEN = 80;

/**
 * Мемоизация. Билдеры URL зовутся ИЗ ШАБЛОНОВ (`[href]="productUrl(item)"`), то
 * есть пересчитываются на каждом цикле change detection: листинг из 92 товаров с
 * парой ссылок в строке — это ~200 транслитераций на тик, при любом движении мыши.
 * Заголовки при этом одни и те же.
 *
 * Кэш **ограничен**: в SSR процесс живёт долго и обслуживает много страниц, поэтому
 * неограниченная Map была бы утечкой. Вытеснение — по вставке (Map хранит порядок),
 * то есть простой FIFO: LRU здесь не нужен, окно и так шире одной страницы листинга.
 */
const CACHE_LIMIT = 500;
const cache = new Map<string, string>();

/**
 * @param header заголовок сущности; `null`/`undefined`/пустой — валидный вход
 * @param maxLen жёсткий потолок длины результата
 * @returns slug из `[a-z0-9-]`; **пустая строка — валидный результат** (URL
 *          тогда строится без slug-сегмента, см. `url-builder`)
 */
export function slugify(header: string | null | undefined, maxLen = DEFAULT_MAX_LEN): string {
  if (!header) {
    return '';
  }

  // Разделитель — экранированный U+0000 (в заголовках его не бывает), а не пробел:
  // иначе ключи ('0 abc', maxLen 8) и (' abc', maxLen 80) не различимы. Escape-форма,
  // а не сырой символ: сырые управляющие символы в исходниках ломают парсеры
  // (в этом проекте уже ловили это на U+2028 в спеках — CLAUDE.md → Testing).
  const cacheKey = `${maxLen}\u0000${header}`;
  const hit = cache.get(cacheKey);
  if (hit !== undefined) {
    return hit;
  }

  // Регистр опускаем ДО транслитерации — иначе таблице нужны заглавные ключи.
  const transliterated = [...header.toLowerCase()]
    .map(char => (char in CYRILLIC ? CYRILLIC[char] : char))
    .join('');

  // Крайние дефисы снимаем двумя якорными заменами, а не альтернативой `^-+|-+$`:
  // приоритет `|` относительно якорей в такой записи неочевиден для читателя (и линтер
  // на этом справедливо ругается), результат же побайтово тот же.
  const slug = transliterated
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+/, '')
    .replace(/-+$/, '');

  const result = truncateAtWordBoundary(slug, maxLen);

  if (cache.size >= CACHE_LIMIT) {
    cache.delete(cache.keys().next().value as string);
  }
  cache.set(cacheKey, result);

  return result;
}

/**
 * Обрезка по границе слова. `maxLen` — **потолок**, а не пожелание: сначала
 * жёсткий рез, потом откат к последнему дефису, чтобы не рубить слово посередине.
 *
 * ТЗ §6.2 формулировал это как «обрезка по границе слова важнее самого числа»,
 * что читалось двояко — можно ли превысить `maxLen`, доводя слово до конца.
 * Решено: нельзя. Иначе гарантии длины пути нет вообще (одно 200-символьное
 * слово дало бы 200-символьный slug), а slug — декоративная часть URL:
 * идентичность несёт ключ в хвосте, поэтому укоротить его безопасно.
 */
function truncateAtWordBoundary(slug: string, maxLen: number): string {
  if (slug.length <= maxLen) {
    return slug;
  }

  const hardCut = slug.slice(0, maxLen);
  const lastBoundary = hardCut.lastIndexOf('-');

  // Дефиса в окне нет (одно длинное слово) — режем жёстко, иначе потолок
  // перестаёт быть потолком.
  return lastBoundary > 0 ? hardCut.slice(0, lastBoundary) : hardCut.replace(/-+$/, '');
}
